import React, { useEffect, useState } from 'react';
import { Modal, Typography, Space, Button, Alert, App, Spin, Checkbox } from 'antd';
import {
  CreditCardOutlined,
  DeleteOutlined,
  ScissorOutlined,
} from '@ant-design/icons';
import { Table } from '../../types/tables';
import { Order } from '../../types/orders';
import { OrderSplit } from '../../types/splits';
import {
  useGetOrderSplitsQuery,
  useCancelOrderSplitsMutation,
  useProcessSplitPaymentMutation,
  useMergeOrderSplitsMutation,
} from '../../services/splitsApi';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import SplitCreator from './SplitCreator';
import PaymentModal, { PaymentEntry } from '../tables/modals/PaymentModal';
import { useDispatch } from 'react-redux';
import { baseApi } from '../../services/baseApi';
import { buildSplitOrder, splitChargedAmount, splitItemsSummary } from '../../utils/splitOrder';
import './splits.css';

const { Text, Title } = Typography;

const money = (n: number) => `$${n.toFixed(2)}`;

interface SplitBillModalProps {
  visible: boolean;
  onCancel: () => void;
  /**
   * Solo llega desde el menú de mesas. Cobrando desde la lista de órdenes no hay
   * objeto Table a mano y la mesa se identifica desde la orden, así que este prop
   * es opcional a propósito.
   */
  table?: Table;
  order?: Order;
  /** Called after the last cuenta is paid and the backend closed the order. */
  onOrderClosed: () => Promise<void> | void;
}

/**
 * División de cuentas de una orden: crea la división (SplitCreator), lista las
 * cuentas con su estado y cobra cada una reutilizando PaymentModal con una
 * "orden sintética" que solo contiene los productos de esa cuenta — los montos
 * prorrateados del backend suman exacto, así que los totales e impresión del
 * PaymentModal funcionan sin cambios. Varias cuentas pendientes se pueden
 * marcar y cobrar juntas: el backend las junta en una sola y se cobra esa.
 * Al pagar la última cuenta el backend cierra la orden y este modal avisa al
 * padre (mesa → Dirty, igual que el cobro normal).
 */
const SplitBillModal: React.FC<SplitBillModalProps> = ({
  visible,
  onCancel,
  table,
  order,
  onOrderClosed,
}) => {
  const { message, modal } = App.useApp();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const dispatch = useDispatch();
  const [creatorVisible, setCreatorVisible] = useState(false);
  const [payingSplit, setPayingSplit] = useState<OrderSplit | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const {
    data: splits,
    isLoading: splitsLoading,
    isError: splitsFailed,
    refetch: refetchSplits,
  } = useGetOrderSplitsQuery(order?.id || '', {
    skip: !order?.id || !visible,
    // Sin esto RTK Query reusa el resultado anterior al volver a suscribirse: un
    // [] cacheado de antes de dividir (o una división creada desde otra caja)
    // haría caer al estado vacío y ofrecer "Dividir Cuenta" sobre una orden ya
    // dividida. El refetch de la lista de órdenes no cubre esto: invalida
    // 'Order', no 'Split'.
    refetchOnMountOrArgChange: true,
  });

  const [cancelOrderSplits, { isLoading: cancelling }] = useCancelOrderSplitsMutation();
  const [processSplitPayment] = useProcessSplitPaymentMutation();
  const [mergeOrderSplits, { isLoading: merging }] = useMergeOrderSplitsMutation();

  // La selección solo vive mientras el modal está abierto; una cuenta que
  // dejó de estar pendiente (la cobró otra caja) sale sola de la selección.
  useEffect(() => {
    if (!visible) setSelectedIds([]);
  }, [visible]);
  useEffect(() => {
    if (!splits) return;
    setSelectedIds(prev => prev.filter(id => splits.some(s => s.id === id && s.status !== 'Paid')));
  }, [splits]);

  if (!order) return null;

  // Sin prop `table` (cobro desde la lista de órdenes) la mesa se deriva de la
  // orden: tableName es el número de mesa y tableId apunta a la mesa real solo en
  // DineIn (TakeOut/Domicilio cuelgan de una mesa virtual).
  const tableSuffix = table
    ? ` — Mesa ${table.number}`
    : order.type === 'DineIn' && order.tableName ? ` — Mesa ${order.tableName}` : '';
  const terminalTableId = table?.id ?? (order.type === 'DineIn' ? order.tableId : undefined);

  const hasSplits = (splits?.length ?? 0) > 0;
  const paidCount = splits?.filter(s => s.status === 'Paid').length ?? 0;
  const pendingSplits = splits?.filter(s => s.status !== 'Paid') ?? [];
  const anyPaid = paidCount > 0;
  const selected = pendingSplits.filter(s => selectedIds.includes(s.id));
  const selectedTotal = selected.reduce((sum, s) => sum + splitChargedAmount(s), 0);

  const handleProcessSplitPayment = async (payments: PaymentEntry[], tip: number) => {
    if (!payingSplit) return;

    const result = await processSplitPayment({
      splitId: payingSplit.id,
      payments: payments.map(p => ({
        methodId: p.methodId,
        amount: p.amount,
        reference: p.reference,
      })),
      tipAmount: tip,
    }).unwrap();

    setPayingSplit(null);
    // La invalidación de tags de processSplitPayment ya refetchea la lista

    if (result.orderClosed) {
      message.success('Todas las cuentas están pagadas. Orden cerrada.');
      await onOrderClosed();
    } else {
      message.success(`${payingSplit.name} pagada`);
    }
  };

  // El webhook de MP ya cobró la cuenta y, si era la última, cerró la orden.
  // Se replica el post-pago de handleProcessSplitPayment sin llamar a la API.
  const handleTerminalSettled = async () => {
    const wasLast = (splits?.filter(s => s.status !== 'Paid').length ?? 0) <= 1;
    const paidName = payingSplit?.name;
    setPayingSplit(null);
    dispatch(baseApi.util.invalidateTags(['Split', 'Order', 'Table', 'CashRegister', 'CashMovement']));

    if (wasLast) {
      message.success('Todas las cuentas están pagadas. Orden cerrada.');
      await onOrderClosed();
    } else if (paidName) {
      message.success(`${paidName} pagada`);
    }
  };

  /** Junta las cuentas marcadas en una sola y abre su cobro. */
  const handlePayTogether = async () => {
    if (selected.length < 2) return;
    try {
      const result = await mergeOrderSplits({ orderId: order.id, splitIds: selected.map(s => s.id) }).unwrap();
      const merged = result.splits[0];
      setSelectedIds([]);
      if (merged) {
        setPayingSplit(merged);
      }
    } catch (error: any) {
      const data = error?.data;
      message.error(data?.validationErrors?.[0] || data?.message || 'No se pudieron juntar las cuentas');
    }
  };

  const toggleSelected = (id: string, checked: boolean) => {
    setSelectedIds(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)));
  };

  const handleCancelDivision = () => {
    modal.confirm({
      title: '¿Cancelar la división de cuenta?',
      content: 'Se elimina el reparto y la orden vuelve a cobrarse completa.',
      okText: 'Sí, cancelar división',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await cancelOrderSplits(order.id).unwrap();
          message.success('División cancelada');
          onCancel();
        } catch (error: any) {
          message.error(error?.data?.message || 'Error al cancelar la división');
        }
      },
    });
  };

  const renderSplitList = () => (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <Text type="secondary">
          {paidCount} de {splits!.length} cuentas pagadas · la orden se cierra al cobrar la última
        </Text>
        {pendingSplits.length >= 2 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Marca varias para cobrarlas juntas
          </Text>
        )}
      </div>

      <div className="split-list">
        {splits!.map((split) => {
          const isPaid = split.status === 'Paid';
          const isSelected = selectedIds.includes(split.id);
          const selectable = !isPaid && pendingSplits.length >= 2;
          return (
            <div
              key={split.id}
              className={`split-row${isPaid ? ' split-row--paid' : ''}${isSelected ? ' split-row--selected' : ''}`}
            >
              {selectable && (
                <Checkbox
                  checked={isSelected}
                  onChange={(e) => toggleSelected(split.id, e.target.checked)}
                  aria-label={`Marcar ${split.name}`}
                />
              )}
              <div
                className="split-row__body"
                onClick={() => selectable && toggleSelected(split.id, !isSelected)}
                style={{ cursor: selectable ? 'pointer' : 'default' }}
              >
                <div className="split-row__head">
                  <span className="split-row__name">{split.name}</span>
                  <span className="split-money">{money(splitChargedAmount(split))}</span>
                </div>
                <div className="split-row__items">{splitItemsSummary(split)}</div>
              </div>
              <div className="split-row__side">
                {isPaid ? (
                  <>
                    <span className="split-status split-status--paid">Pagada</span>
                    {split.paidAt && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatInTimeZone(split.paidAt, 'HH:mm')}
                      </Text>
                    )}
                  </>
                ) : (
                  <Button
                    type="primary"
                    icon={<CreditCardOutlined />}
                    disabled={selected.length >= 2 && !isSelected}
                    onClick={() => setPayingSplit(split)}
                  >
                    Cobrar
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selected.length >= 2 && (
        <div className="split-bar">
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Text strong>{selected.length} cuentas juntas</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {selected.map(s => s.name.replace(/^Cuentas?\s+/, '')).join(' + ')} · <span className="split-money">{money(selectedTotal)}</span>
            </Text>
          </div>
          <Space>
            <Button onClick={() => setSelectedIds([])}>Quitar marca</Button>
            <Button type="primary" icon={<CreditCardOutlined />} loading={merging} onClick={handlePayTogether}>
              Cobrar juntas
            </Button>
          </Space>
        </div>
      )}

      {!anyPaid && (
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          loading={cancelling}
          onClick={handleCancelDivision}
          style={{ marginTop: 12 }}
        >
          Cancelar división
        </Button>
      )}
    </>
  );

  // Si la consulta falló no sabemos si la orden ya está dividida, y caer al
  // estado vacío invita a dividir otra vez: la segunda división la rechaza el
  // backend ("La orden ya tiene la cuenta dividida") desde la validación,
  // dejando a quien cobra sin salida. Ante el error se ofrece reintentar,
  // nunca dividir.
  const renderError = () => (
    <div style={{ textAlign: 'center', padding: '32px 16px' }}>
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar las cuentas"
        description="No sabemos si esta orden ya está dividida. Reintenta antes de dividirla de nuevo."
        style={{ marginBottom: 16, textAlign: 'left' }}
      />
      <Button type="primary" onClick={() => refetchSplits()}>
        Reintentar
      </Button>
    </div>
  );

  const renderEmpty = () => (
    <div style={{ textAlign: 'center', padding: '32px 16px' }}>
      <ScissorOutlined style={{ fontSize: 40, color: 'var(--border-color-strong)' }} />
      <Title level={5} style={{ marginTop: 16 }}>
        Divide la cuenta por comensal, en partes iguales o por productos
      </Title>
      <Text type="secondary">
        Cada cuenta se cobra por separado, con su propio método de pago y propina.
      </Text>
      <div style={{ marginTop: 24 }}>
        <Button type="primary" size="large" icon={<ScissorOutlined />} onClick={() => setCreatorVisible(true)}>
          Dividir cuenta
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        title={`División de Cuenta${tableSuffix}`}
        open={visible && !payingSplit}
        onCancel={onCancel}
        footer={null}
        width={640}
        destroyOnClose
      >
        {splitsLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : splitsFailed ? renderError() : hasSplits ? renderSplitList() : renderEmpty()}
      </Modal>

      <SplitCreator
        visible={creatorVisible}
        orderId={order.id}
        orderData={order}
        onComplete={() => {
          // createOrderSplits invalida 'Split'; la lista se refetchea sola
          setCreatorVisible(false);
        }}
        onCancel={() => setCreatorVisible(false)}
      />

      {payingSplit && (
        <PaymentModal
          visible={!!payingSplit}
          onCancel={() => setPayingSplit(null)}
          table={table}
          order={buildSplitOrder(order, payingSplit)}
          autofacturaCuenta={payingSplit.splitNumber}
          onProcessPayment={handleProcessSplitPayment}
          terminalTarget={{ splitId: payingSplit.id, tableId: terminalTableId }}
          onTerminalPaymentSettled={handleTerminalSettled}
        />
      )}
    </>
  );
};

export default SplitBillModal;
